import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import apiClient from '../../services/api';
import { useT } from '../../context/LanguageContext';

/* The live capacity view, drawn by hand in SVG; it belongs to a DATE, and one hue carries every mark. */
const LIVE_INK = '#ea580c';        // orange-600 — the mark
const LIVE_OVER = '#dc2626';       // red-600 — over capacity
const EMPTY_INK = '#e2e8f0';       // slate-200 — the unfilled remainder

function OccupancyDonut({ used, capacity, over }) {
  const R = 54, C = 2 * Math.PI * R;
  const pct = capacity ? Math.min(100, (used * 100) / capacity) : 0;
  // 2px of surface between the arcs, so they read as segments rather than one ring changing colour.
  const gap = capacity ? 2 : 0;
  const filled = Math.max(0, (pct / 100) * C - gap);
  return (
    <div className="flex items-center gap-4">
      <svg width="132" height="132" viewBox="0 0 132 132" role="img"
        aria-label={capacity ? `${Math.round(pct)} percent full` : 'No capacity planned'}>
        <circle cx="66" cy="66" r={R} fill="none" stroke={EMPTY_INK} strokeWidth="14" />
        {capacity > 0 && (
          <circle
            cx="66" cy="66" r={R} fill="none"
            stroke={over ? LIVE_OVER : LIVE_INK}
            strokeWidth="14" strokeLinecap="round"
            strokeDasharray={`${filled} ${C - filled}`}
            transform="rotate(-90 66 66)"
          />
        )}
        {/* The hero number sits in the hole: a donut without its value makes the reader estimate an angle. */}
        <text x="66" y="62" textAnchor="middle" className="fill-foreground"
          style={{ fontSize: 26, fontWeight: 800 }}>
          {capacity ? `${Math.round(pct)}%` : '—'}
        </text>
        <text x="66" y="82" textAnchor="middle" className="fill-muted-foreground"
          style={{ fontSize: 11 }}>
          {capacity ? 'full' : 'no capacity set'}
        </text>
      </svg>
      <div className="text-sm space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: over ? LIVE_OVER : LIVE_INK }} />
          <span className="font-semibold text-foreground tabular-nums">{used}</span>
          <span className="text-muted-foreground">in the hall now</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: EMPTY_INK }} />
          <span className="font-semibold text-foreground tabular-nums">
            {capacity ? Math.max(0, capacity - used) : '—'}
          </span>
          <span className="text-muted-foreground">
            {capacity ? 'places left' : 'set covers on this date'}
          </span>
        </div>
        {over && (
          <p className="text-xs font-medium" style={{ color: LIVE_OVER }}>
            Over the planned {capacity} covers.
          </p>
        )}
      </div>
    </div>
  );
}

/* Covers in the hall hour by hour: one series, so no legend, with only the last point labelled. */
function OccupancyLine({ series, capacity }) {
  const [hover, setHover] = useState(null);
  if (!series.length) {
    return <p className="text-sm text-muted-foreground">Nothing sold yet today — the line starts with the first cover.</p>;
  }
  const W = 560, H = 180, PADL = 34, PADB = 26, PADT = 12;
  const top = Math.max(capacity || 0, ...series.map((p) => p.covers), 1);
  const x = (i) => PADL + (i * (W - PADL - 12)) / Math.max(1, series.length - 1);
  const y = (v) => PADT + (1 - v / top) * (H - PADT - PADB);
  const path = series.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.covers)}`).join(' ');
  return (
    <div className="relative">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label="Covers in the hall over the service"
        onMouseLeave={() => setHover(null)}>
        {/* Recessive gridlines — present, never competing with the data. */}
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PADL} x2={W - 12} y1={y(top * f)} y2={y(top * f)} stroke="#f1f5f9" strokeWidth="1" />
            <text x={PADL - 6} y={y(top * f) + 3} textAnchor="end"
              className="fill-muted-foreground" style={{ fontSize: 10 }}>
              {Math.round(top * f)}
            </text>
          </g>
        ))}
        {/* The planned capacity as a reference, so "how full" is readable off the line and not just the donut. */}
        {capacity > 0 && capacity <= top && (
          <line x1={PADL} x2={W - 12} y1={y(capacity)} y2={y(capacity)}
            stroke={LIVE_OVER} strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
        )}
        <path d={path} fill="none" stroke={LIVE_INK} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
        {series.map((p, i) => (
          <g key={p.hour}>
            <circle cx={x(i)} cy={y(p.covers)} r={hover === i ? 5 : 3.5}
              fill={LIVE_INK} stroke="#fff" strokeWidth="2" />
            {/* Hit target larger than the mark. */}
            <rect x={x(i) - 14} y={PADT} width="28" height={H - PADT - PADB}
              fill="transparent" onMouseEnter={() => setHover(i)} />
            <text x={x(i)} y={H - 8} textAnchor="middle"
              className="fill-muted-foreground" style={{ fontSize: 10 }}>{p.label}</text>
          </g>
        ))}
      </svg>
      {hover != null && (
        <div className="pointer-events-none absolute top-0 left-0 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-sm"
          style={{ transform: `translate(${(x(hover) / W) * 100}%, -6px)` }}>
          <div className="font-semibold text-foreground">{series[hover].label}</div>
          <div className="text-muted-foreground tabular-nums">
            {series[hover].covers} in · {series[hover].arrived} arrived
          </div>
        </div>
      )}
    </div>
  );
}

/* One figure with its label - four of these answer "booked, came, coming, left" without reading the table. */
function Stat({ label, value, hint, tone }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone === 'warn' ? 'text-red-600' : 'text-foreground'}`}>
        {value}
        {hint && <span className="ml-1 text-[11px] font-normal text-muted-foreground">{hint}</span>}
      </div>
    </div>
  );
}

/* A booking's state in the room's words: on a day that is over, one still "confirmed" simply never came. */
function bookingState(status, isPast) {
  if (status === 'SEATED' || status === 'COMPLETED') return ['Arrived', 'bg-emerald-50 text-emerald-700'];
  if (status === 'NO_SHOW') return ['No-show', 'bg-red-50 text-red-700'];
  if (status === 'CANCELLED') return ['Cancelled', 'bg-muted text-muted-foreground'];
  if (isPast) return ['Never came', 'bg-red-50 text-red-700'];
  return status === 'CONFIRMED'
    ? ['Still to come', 'bg-sky-50 text-sky-700']
    : ['Awaiting approval', 'bg-amber-50 text-amber-700'];
}

export default function CapacityPanel({ restaurantId, buffet, date }) {
  const [live, setLive] = useState(null);
  const [err, setErr] = useState('');
  /* Selling a party in, right where its effect shows - this strip is the write behind every number on the panel. */
  const [pAdults, setPAdults] = useState(2);
  const [pKids, setPKids] = useState(0);
  const [pPayment, setPPayment] = useState('CASH');
  const [selling, setSelling] = useState(false);

  const pull = () => apiClient
    .get(`/users/restaurant/${restaurantId}/buffets/${buffet.id}/live/`,
      { params: date ? { date } : {} })
    .then(({ data }) => { if (data?.success) { setLive(data.data); setErr(''); } })
    .catch(() => setErr('Could not read the room just now.'));

  useEffect(() => {
    let alive = true;
    const safePull = () => { if (alive) pull(); };
    safePull();
    // A board left open must stay true, but only today's can change; polling a finished day asks a settled question.
    if (date && date !== new Date().toLocaleDateString('en-CA')) return () => { alive = false; };
    const t = setInterval(safePull, 30000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, buffet.id, date]);

  const sellParty = async () => {
    const adults = Math.max(0, Number(pAdults) || 0);
    const children = Math.max(0, Number(pKids) || 0);
    if (adults + children <= 0) return;
    setSelling(true);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${buffet.id}/covers/`,
        { adults, children, payment_method: pPayment });
      if (!data?.success) throw new Error(data?.error || 'Could not record the party');
      setPAdults(2); setPKids(0);
      await pull();                       // the donut answers immediately
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Could not record the party');
    } finally {
      setSelling(false);
    }
  };

  if (err && !live) return <p className="text-sm text-red-600">{err}</p>;
  if (!live) return <p className="text-sm text-muted-foreground">Reading the room…</p>;

  const bookings = live.bookings || [];
  // Defaults spread under the server's answer, so a tile reads 0 rather than blank against an older build.
  const sum = {
    capacity: live.capacity || 0,
    booked_total: 0,
    booked_heads: 0,
    arrived_heads: 0,
    no_show_heads: 0,
    covers_sold: live.in_now || 0,
    seats_free: live.free,
    ...(live.booking_summary || {}),
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {live.is_past ? 'How full it got' : 'How full it is now'}
          </h4>
          <OccupancyDonut used={live.in_now} capacity={live.capacity} over={live.over_capacity} />
          <p className="mt-3 text-xs text-muted-foreground">
            {live.adults} adults · {live.children} kids
            {live.is_past ? ' came that day.' : ' in the hall.'}
            {live.booked_covers > 0 && (live.is_past
              ? <> {live.booked_covers} booked but never checked in.</>
              : <> {live.booked_covers} more booked to arrive.</>)}
          </p>

          {/* Sell a party in; hidden on a past day, since a sale is stamped now and would corrupt both days. */}
          {!live.is_past && (
          <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Party walking in
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-muted-foreground">
                Adults
                <input type="number" min="0" value={pAdults}
                  onChange={(e) => setPAdults(e.target.value)}
                  className="mt-1 block w-16 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground tabular-nums" />
              </label>
              <label className="text-xs text-muted-foreground">
                Kids
                <input type="number" min="0" value={pKids}
                  onChange={(e) => setPKids(e.target.value)}
                  className="mt-1 block w-16 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground tabular-nums" />
              </label>
              <label className="text-xs text-muted-foreground">
                Payment
                <select value={pPayment} onChange={(e) => setPPayment(e.target.value)}
                  className="mt-1 block rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground">
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
              <button
                type="button"
                onClick={sellParty}
                disabled={selling || (Number(pAdults) || 0) + (Number(pKids) || 0) <= 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                <Users size={14} /> {selling ? 'Recording…' : 'Sell covers'}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Charged at the per-head price in force now.
            </p>
            {err && <p className="mt-1.5 text-xs text-red-600">{err}</p>}
          </div>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Covers in the hall, through the service
          </h4>
          <OccupancyLine series={live.series || []} capacity={live.capacity} />
        </div>
      </div>

      {/* The diary against the door: when each party is due, which of them came, and what is still sellable. */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Bookings {live.is_past ? 'that day' : 'today'} ({bookings.length})
        </h4>

        <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/30 p-3 sm:grid-cols-4">
          <Stat label="Heads booked" value={sum.booked_total} />
          {/* Everyone the till counted in, walk-ins included - the honest read on who is actually eating. */}
          <Stat label={live.is_past ? 'Attended' : 'Attended so far'} value={sum.covers_sold} />
          <Stat label={live.is_past ? 'Never came' : 'Still to come'}
            value={live.is_past ? sum.no_show_heads + sum.booked_heads : sum.booked_heads}
            tone={live.is_past && (sum.no_show_heads + sum.booked_heads) > 0 ? 'warn' : undefined} />
          {/* Seats nobody has claimed: the room, less everybody through the door and everybody still holding a booking. */}
          <Stat label="Seats free"
            value={sum.seats_free == null ? '—' : sum.seats_free}
            hint={sum.capacity ? `of ${sum.capacity}` : 'no capacity set'} />
        </div>

        {bookings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {live.is_past
              ? 'Nothing was booked for this buffet that day.'
              : 'No buffet bookings today — every cover so far walked in.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-medium py-1.5 pr-4">Time</th>
                  <th className="text-left font-medium py-1.5 pr-4">Guest</th>
                  <th className="text-left font-medium py-1.5 pr-4">Party</th>
                  <th className="text-left font-medium py-1.5 pr-4">Table</th>
                  <th className="text-left font-medium py-1.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {bookings.map((r) => {
                  const [label, cls] = bookingState(r.status, live.is_past);
                  return (
                    <tr key={r.id}>
                      <td className="py-1.5 pr-4 font-medium tabular-nums">{r.time_label}</td>
                      <td className="py-1.5 pr-4">{r.name}</td>
                      <td className="py-1.5 pr-4 tabular-nums">
                        {r.pax}{r.kids > 0 && <span className="text-muted-foreground"> ({r.adults}+{r.kids} kids)</span>}
                      </td>
                      <td className="py-1.5 pr-4 text-muted-foreground">{r.table_code || '—'}</td>
                      <td className="py-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
                          {label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
